function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t["return"] && (u = t["return"](), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
import React from "react";
import moment from "moment-timezone";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import { FaRegCalendarAlt, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { arrayToStringOutput } from "./../SideMenu/Header";
import "./PayrollCalculator.css";

/* Maps the payrollWeekStartsOn enum to moment's day-of-week index (0 = Sunday) */
var WEEK_START_DAY_INDEX = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6
};

/* Formats a start/end moment pair like "Jan 5–11" or "Jan 26 – Feb 1" */
var formatPeriodLabel = function formatPeriodLabel(start, end) {
  if (start.month() === end.month()) {
    return "".concat(start.format("MMM"), " ").concat(start.format("D"), "\u2013").concat(end.format("D"));
  }
  return "".concat(start.format("MMM D"), " \u2013 ").concat(end.format("MMM D"));
};

/* Display metadata per payroll cycle. `days` is the fixed window length for the
   date-aligned cycles (weekly / bi-weekly); the month-based cycles compute their
   own boundaries. */
export var PAYROLL_CYCLE_META = {
  weekly: {
    label: "Weekly",
    days: 7
  },
  "bi-weekly": {
    label: "Bi-weekly",
    days: 14
  },
  "semi-monthly": {
    label: "Semi-monthly"
  },
  monthly: {
    label: "Monthly"
  }
};

/* Generates fixed-length (7-day weekly / 14-day bi-weekly) periods for a calendar
   year. Each window is aligned to weekStartsOn; the first period is the first
   weekStartsOn weekday on or after Jan 1. Returns [{ start, end, label }]. */
var generateAlignedPeriods = function generateAlignedPeriods(year, weekStartsOn, timezone, periodDays) {
  var _WEEK_START_DAY_INDEX;
  // payrollWeekStartsOn is stored lowercase (e.g. "tuesday"); the index map keys
  // are uppercase, so normalise before lookup to avoid falling back to Monday.
  var startDow = (_WEEK_START_DAY_INDEX = WEEK_START_DAY_INDEX[String(weekStartsOn).toUpperCase()]) !== null && _WEEK_START_DAY_INDEX !== void 0 ? _WEEK_START_DAY_INDEX : 1;
  var tz = timezone || moment.tz.guess();
  var cursor = moment.tz([year, 0, 1], tz).startOf("day");
  while (cursor.day() !== startDow) {
    cursor.add(1, "day");
  }
  var periods = [];
  while (cursor.year() === year) {
    var start = cursor.clone();
    var end = cursor.clone().add(periodDays - 1, "days");
    periods.push({
      start: start,
      end: end,
      label: formatPeriodLabel(start, end)
    });
    cursor.add(periodDays, "days");
  }
  return periods;
};

/* Generates the 12 monthly periods (1st → last day) for a calendar year. */
var generateMonthlyPeriods = function generateMonthlyPeriods(year, timezone) {
  var tz = timezone || moment.tz.guess();
  var periods = [];
  for (var month = 0; month < 12; month++) {
    var start = moment.tz([year, month, 1], tz).startOf("day");
    var end = start.clone().endOf("month").startOf("day");
    periods.push({
      start: start,
      end: end,
      label: start.format("MMMM")
    });
  }
  return periods;
};

/* Generates the 24 semi-monthly periods (1st–15th and 16th–last day) for a year. */
var generateSemiMonthlyPeriods = function generateSemiMonthlyPeriods(year, timezone) {
  var tz = timezone || moment.tz.guess();
  var periods = [];
  for (var month = 0; month < 12; month++) {
    var first = moment.tz([year, month, 1], tz).startOf("day");
    var mid = moment.tz([year, month, 15], tz).startOf("day");
    var second = moment.tz([year, month, 16], tz).startOf("day");
    var end = first.clone().endOf("month").startOf("day");
    periods.push({
      start: first,
      end: mid,
      label: formatPeriodLabel(first, mid)
    });
    periods.push({
      start: second,
      end: end,
      label: formatPeriodLabel(second, end)
    });
  }
  return periods;
};

/* Cycle-aware dispatcher: returns the selectable payroll periods for a calendar
   year based on the organisation's payrollCycle. Falls back to weekly. */
export var generatePeriods = function generatePeriods(cycle, year, weekStartsOn, timezone) {
  switch (cycle) {
    case "bi-weekly":
      return generateAlignedPeriods(year, weekStartsOn, timezone, PAYROLL_CYCLE_META["bi-weekly"].days);
    case "semi-monthly":
      return generateSemiMonthlyPeriods(year, timezone);
    case "monthly":
      return generateMonthlyPeriods(year, timezone);
    case "weekly":
    default:
      return generateAlignedPeriods(year, weekStartsOn, timezone, PAYROLL_CYCLE_META.weekly.days);
  }
};

/* Resolves the {startDate, endDate} of the payroll period that contains today for
   the organisation's configured cycle — the same "current period" the picker
   highlights. Returns null when no cycle is configured or no period matches. */
export var getCurrentPeriodDates = function getCurrentPeriodDates(organisationSettings, fallbackTimezone) {
  var cycle = organisationSettings === null || organisationSettings === void 0 ? void 0 : organisationSettings.payrollCycle;
  // "not-applicable" (and empty/null) means no cycle — no current period to snap
  // to, so callers fall back to their default (e.g. current month).
  if (!cycle || cycle === "not-applicable") return null;
  var weekStartsOn = (organisationSettings === null || organisationSettings === void 0 ? void 0 : organisationSettings.payrollWeekStartsOn) || "MONDAY";
  // Anchor periods in the viewer's timezone (matching the month-default logic and
  // arrayToStringOutput), so the stored end date renders on the same day it spans.
  var tz = fallbackTimezone || moment.tz.guess();
  var today = moment().tz(tz);
  var current = generatePeriods(cycle, today.year(), weekStartsOn, tz).find(function (p) {
    return today.isBetween(p.start, moment(p.end).endOf("day"), null, "[]");
  });
  if (!current) return null;
  return {
    startDate: current.start.toDate(),
    endDate: current.end.clone().endOf("day").toDate()
  };
};

/* Cycle-aware payroll period picker. Renders a trigger button showing the active
   period and a popover of selectable periods for a navigable year. */
var PayrollPeriodPicker = function PayrollPeriodPicker(props) {
  var _PAYROLL_CYCLE_META$p;
  var startDate = props.startDate,
    endDate = props.endDate,
    onFilterChange = props.onFilterChange,
    hideOverlay = props.hideOverlay,
    organisationSettings = props.organisationSettings;

  // Display timezone (org) is only used for formatting the trigger label; periods
  // are anchored in the viewer's timezone so their boundaries render on the same
  // calendar days shown in the grid (avoids an off-by-one on the end date).
  var timezone = organisationSettings === null || organisationSettings === void 0 ? void 0 : organisationSettings.organisationTimezone;
  var periodTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  var weekStartsOn = (organisationSettings === null || organisationSettings === void 0 ? void 0 : organisationSettings.payrollWeekStartsOn) || "MONDAY";
  var payrollCycle = (organisationSettings === null || organisationSettings === void 0 ? void 0 : organisationSettings.payrollCycle) || "weekly";
  var selectedYear = moment(startDate).year();
  var _React$useState = React.useState(selectedYear),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    viewYear = _React$useState2[0],
    setViewYear = _React$useState2[1];
  var cycleLabel = ((_PAYROLL_CYCLE_META$p = PAYROLL_CYCLE_META[payrollCycle]) === null || _PAYROLL_CYCLE_META$p === void 0 ? void 0 : _PAYROLL_CYCLE_META$p.label) || "Weekly";
  var periods = React.useMemo(function () {
    return generatePeriods(payrollCycle, viewYear, weekStartsOn, periodTimezone);
  }, [payrollCycle, viewYear, weekStartsOn, periodTimezone]);
  var isSelected = function isSelected(period) {
    return moment(period.start).isSame(moment(startDate), "day");
  };
  var isCurrent = function isCurrent(period) {
    var today = moment().tz(periodTimezone);
    return today.isBetween(period.start, moment(period.end).endOf("day"), null, "[]");
  };
  var activeIsCurrent = periods.some(function (p) {
    return isSelected(p) && isCurrent(p);
  });
  var hasSelectedInView = periods.some(isSelected);
  var handleSelect = function handleSelect(period) {
    onFilterChange({
      startDate: period.start.toDate(),
      endDate: period.end.clone().endOf("day").toDate()
    });
    hideOverlay();
  };
  var jumpToCurrent = function jumpToCurrent() {
    var currentYear = moment().tz(periodTimezone).year();
    if (currentYear !== viewYear) {
      setViewYear(currentYear);
      return;
    }
    var current = generatePeriods(payrollCycle, currentYear, weekStartsOn, periodTimezone).find(isCurrent);
    if (current) handleSelect(current);
  };

  // On open, scroll the grid so the active period is centered (a year of periods
  // often pushes it below the fold). Ref callback fires when the item mounts;
  // only scrolls when it isn't already fully visible, to avoid jitter.
  var focusActiveItem = function focusActiveItem(node, active) {
    if (!node || !active) return;
    var grid = node.closest(".payroll-period-picker-grid");
    if (!grid) return;
    var gridRect = grid.getBoundingClientRect();
    var nodeRect = node.getBoundingClientRect();
    var fullyVisible = nodeRect.top >= gridRect.top && nodeRect.bottom <= gridRect.bottom;
    if (!fullyVisible) {
      grid.scrollTop += nodeRect.top - gridRect.top - (grid.clientHeight - node.offsetHeight) / 2;
    }
  };
  return /*#__PURE__*/React.createElement(OverlayTrigger, {
    trigger: "click",
    rootClose: true,
    placement: "bottom-start",
    overlay: /*#__PURE__*/React.createElement(Popover, {
      className: "payroll-period-picker-popover"
    }, /*#__PURE__*/React.createElement(Popover.Content, {
      className: "payroll-period-picker-content"
    }, /*#__PURE__*/React.createElement("div", {
      className: "payroll-period-picker-header"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "payroll-period-picker-nav",
      onClick: function onClick() {
        return setViewYear(function (y) {
          return y - 1;
        });
      }
    }, /*#__PURE__*/React.createElement(FaChevronLeft, null)), /*#__PURE__*/React.createElement("div", {
      className: "payroll-period-picker-header-title"
    }, /*#__PURE__*/React.createElement("span", {
      className: "payroll-period-picker-year"
    }, viewYear), /*#__PURE__*/React.createElement("span", {
      className: "payroll-period-picker-subtitle"
    }, cycleLabel, " Periods")), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "payroll-period-picker-nav",
      onClick: function onClick() {
        return setViewYear(function (y) {
          return y + 1;
        });
      }
    }, /*#__PURE__*/React.createElement(FaChevronRight, null))), /*#__PURE__*/React.createElement("div", {
      className: "payroll-period-picker-grid"
    }, periods.map(function (period) {
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: period.start.format("YYYY-MM-DD"),
        ref: function ref(node) {
          return focusActiveItem(node, isSelected(period) || !hasSelectedInView && isCurrent(period));
        },
        className: "payroll-period-picker-item" + (isSelected(period) ? " payroll-period-picker-item-active" : ""),
        onClick: function onClick() {
          return handleSelect(period);
        }
      }, period.label);
    })), /*#__PURE__*/React.createElement("div", {
      className: "payroll-period-picker-footer"
    }, /*#__PURE__*/React.createElement("span", {
      className: "payroll-period-picker-count"
    }, periods.length, " periods in ", viewYear), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "payroll-period-picker-jump",
      onClick: jumpToCurrent
    }, "Jump to current"))))
  }, /*#__PURE__*/React.createElement("div", {
    id: "date-filter",
    className: "payroll-summary-header-filter-date"
  }, /*#__PURE__*/React.createElement(FaRegCalendarAlt, {
    className: "page-content-wrapper-menu-filter-date-icon"
  }), /*#__PURE__*/React.createElement("p", {
    className: "payroll-summary-header-filter-date-text"
  }, arrayToStringOutput([startDate, endDate], timezone)), /*#__PURE__*/React.createElement("span", {
    className: "payroll-period-picker-tag"
  }, "(", cycleLabel, activeIsCurrent ? " · Current" : "", ")")));
};
export default PayrollPeriodPicker;